import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import Cookies from 'js-cookie';
import { Observable } from 'rxjs';
import {
  API_COURSES_ARCHIVED,
  API_COURSES_DRAFT,
  API_COURSES_IN_REVIEW,
  API_COURSES_PUBLISHED,
  API_COURSES_REJECTED,
  API_COURSES_REVIEW_OR_PUBLISHED,
  API_COURSES_WITHDRAW,
  API_PROJECT_GET,
  Project,
} from '../../models';
import { HttpService } from '../http';
import { Logger } from '../logger';
import { ModalService } from '../modal.service';

const log = new Logger('ProjectService');

export type ToStateResult = { project: Project };

/**
 * Extract a user-facing message from a drf_standardized_errors response.
 * Falls back to a generic string when the shape doesn't match — never
 * returns undefined, so callers can always render a toast/modal.
 */
function extractErrorMessage(err: unknown, fallback: string): string {
  const first = (err as { error?: { errors?: Array<{ detail?: string }> } })?.error?.errors?.[0];
  return first?.detail || fallback;
}

let DEFAULT_LANGUAGE = 'ar-SA';

// Maps 2-char cookie codes to full locale codes and vice versa
const LOCALE_MAP: { [key: string]: string } = {
  en: 'en-US',
  'en-US': 'en-US',
  ar: 'ar-SA',
  'ar-SA': 'ar-SA',
};

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  private http = inject(HttpService);
  private modalService = inject(ModalService);
  private router = inject(Router);
  private readonly store = inject(Store);
  private platformId = inject(PLATFORM_ID);
  private document = inject(DOCUMENT);

  getProject(projectId: string): Observable<Project> {
    return this.http.get<Project>(API_PROJECT_GET.replace('<PROJECT_ID>', projectId));
  }

  get language(): string {
    let language;
    if (isPlatformBrowser(this.platformId)) {
      const cookieLang = Cookies.get('lang');
      language = (cookieLang ? LOCALE_MAP[cookieLang] : null) || localStorage.getItem('thk_lang') || DEFAULT_LANGUAGE;
    }
    return language ?? DEFAULT_LANGUAGE;
  }

  // actor: instructor
  toStateDraft(projectId: string): void {
    this.modalService.confirm({
      title: $localize`Edit Course`,
      message: $localize`Are you sure you want to edit and set this course to Draft?`,
      onConfirm: () => {
        this.http.post<ToStateResult>(API_COURSES_DRAFT.replace('<PROJECT_ID>', projectId), {}).subscribe((data) => {
          // this.store.dispatch(CourseActionsV2.removeCourseDetails());

          // Store flag to show status modal after page navigation
          sessionStorage.setItem('course-action-completed', JSON.stringify({
            type: 'draft',
            userName: 'You',
            timestamp: Date.now(),
          }));

          // Navigate to the draft course
          if (isPlatformBrowser(this.platformId)) {
            const language = this.language === 'en-US' ? 'en' : 'ar';
            const pathname = `/${language}/instructor/course/${data.project.unpublished_course}/intended-learners`;
            const win = this.document.defaultView;
            if (win) {
              win.location.replace(pathname);
            }
          }
        });
      },
    });
  }

  // actor: instructor
  toStateInReview(projectId: string): Observable<ToStateResult> {
    return this.http.post<ToStateResult>(API_COURSES_IN_REVIEW.replace('<PROJECT_ID>', projectId), {});
  }

  toStatePublishedLive(projectId: string): void {
    this.http
      .post<ToStateResult>(API_COURSES_PUBLISHED.replace('<PROJECT_ID>', projectId), {})
      .subscribe({
        next: (data: any) => {
          // Store flag to show status modal after page navigation
          sessionStorage.setItem('course-action-completed', JSON.stringify({
            type: 'published',
            userName: 'You',
            timestamp: Date.now(),
            publishedCourseId: data.project?.published_course,
            unpublishedCourseId: data.project?.unpublished_course,
          }));

          // Navigate to the published course in preview mode
          if (isPlatformBrowser(this.platformId)) {
            const win = this.document.defaultView;
            if (win) {
              const language = win.location.pathname.startsWith('/ar') ? 'ar' : 'en';
              const publishedCourseId = data.project?.published_course || data.project?.unpublished_course;
              const pathname = `/${language}/instructor/course/${publishedCourseId}/intended-learners?preview=true`;
              win.location.replace(pathname);
            }
          }
        },
        error: (err) => {
          this.modalService.message({
            title: $localize`Couldn't publish course`,
            message: extractErrorMessage(err, $localize`Something went wrong while publishing. Please try again or contact support.`),
          });
        },
      });
  }

  // actor: instructor /courses/projects/{id}/to_state_published/
  toStateReviewOrPublished(projectId: string): void {
    this.modalService.setLoading(true);
    this.http
      .post<ToStateResult>(API_COURSES_REVIEW_OR_PUBLISHED.replace('<PROJECT_ID>', projectId), {})
      .subscribe({
        next: (data: any) => {
          this.modalService.close();

          // Store flag to show status modal after page navigation
          sessionStorage.setItem('course-action-completed', JSON.stringify({
            type: 'published',
            userName: 'You',
            timestamp: Date.now(),
            publishedCourseId: data.project?.published_course,
            unpublishedCourseId: data.project?.unpublished_course,
          }));

          // Navigate to the published course in preview mode
          if (isPlatformBrowser(this.platformId)) {
            const win = this.document.defaultView;
            if (win) {
              const language = win.location.pathname.startsWith('/ar') ? 'ar' : 'en';
              const publishedCourseId = data.project?.published_course || data.project?.unpublished_course;
              const pathname = `/${language}/instructor/course/${publishedCourseId}/intended-learners?preview=true`;
              win.location.replace(pathname);
            }
          }
        },
        error: (err) => {
          this.modalService.close();
          this.modalService.message({
            title: $localize`Couldn't submit course`,
            message: extractErrorMessage(err, $localize`Something went wrong while submitting. Please try again or contact support.`),
          });
        },
      });
  }

  // actor: admin
  toStatePublished(projectId: string): void {
    this.modalService.confirm({
      title: $localize`Publish Course`,
      message: $localize`Are you sure you want to publish this course?`,
      onConfirm: () => {
        this.http
          .post<ToStateResult>(API_COURSES_PUBLISHED.replace('<PROJECT_ID>', projectId), {})
          .subscribe((data) => {
            this.router.navigate([`/course/${data.project.unpublished_course}/details`]);
            this.modalService.message({
              title: $localize`Course Published`,
              message: $localize`This course is now live and available to learners`,
            });
          });
      },
    });
  }

  // actor: admin
  toStateRejected(projectId: string): void {
    this.modalService.confirm({
      title: $localize`Reject Course`,
      message: $localize`Are you sure you want to reject this course?`,
      onConfirm: () => {
        this.http.post<ToStateResult>(API_COURSES_REJECTED.replace('<PROJECT_ID>', projectId), {}).subscribe((data) => {
          this.router.navigate([`/course/${data.project.unpublished_course}/details`]);
          this.modalService.message({
            title: $localize`Course Rejected`,
            message: $localize`This course is now rejected and instructor will be notified`,
          });
        });
      },
    });
  }

  toStateArchived(projectId: string): Observable<ToStateResult> {
    return this.http.post<ToStateResult>(API_COURSES_ARCHIVED.replace('<PROJECT_ID>', projectId), {});
  }

  withdrawSubmission(projectId: string): Observable<ToStateResult> {
    return this.http.post<ToStateResult>(API_COURSES_WITHDRAW.replace('<PROJECT_ID>', projectId), {});
  }
}
